import { Box as MuiBox, type BoxProps as MuiBoxProps, styled, type Theme } from '@mui/material'

import { spacing, type TSpacing } from '@/themes/tokens'

export type BoxVariant =
  | 'flex'
  | 'flexBetween'
  | 'flexCenter'
  | 'flexColumn'
  | 'flexEnd'
  | 'stickyHeader'
  | 'pageRow'
  | 'fullPage'
  | 'spacer'

export interface BoxProps extends Omit<MuiBoxProps, 'sx' | 'gap'> {
  variant?: BoxVariant
  /**
   * Override the variant's default gap using a spacing token.
   * Works on any flex variant that defines a gap.
   */
  gap?: TSpacing
  /**
   * Controls whether flexBetween/flexColumn/flexEnd apply width: 100%.
   * Defaults to true. Pass false for inline/content-width layouts.
   */
  fullWidth?: boolean
}

const getVariantStyles = (theme: Theme, variant?: BoxVariant, gap?: TSpacing, fullWidth?: boolean) => {
  const resolvedGap = gap !== undefined ? theme.spacing(spacing[gap]) : undefined
  const isFullWidth = fullWidth !== false

  switch (variant) {
    case 'flex':
      return {
        display: 'flex',
        alignItems: 'center',
        gap: resolvedGap ?? theme.spacing(spacing.sm),
      }
    case 'flexBetween':
      return {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: resolvedGap ?? theme.spacing(spacing.sm),
        ...(isFullWidth && { width: '100%' }),
      }
    case 'flexCenter':
      return {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }
    case 'flexColumn':
      return {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: resolvedGap ?? theme.spacing(spacing.sm),
        ...(isFullWidth && { width: '100%' }),
      }
    case 'flexEnd':
      return {
        display: 'flex',
        justifyContent: 'flex-end',
        ...(isFullWidth && { width: '100%' }),
      }
    case 'stickyHeader':
      return {
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        position: 'sticky' as const,
        top: 0,
        zIndex: theme.zIndex.appBar,
      }
    case 'pageRow':
      return {
        maxWidth: 1152,
        margin: '0 auto',
        padding: theme.spacing(2, 6),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: resolvedGap ?? theme.spacing(spacing.lg),
      }
    case 'fullPage':
      return {
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        backgroundColor: theme.palette.background.default,
      }
    case 'spacer':
      return {
        flex: 1,
      }
    default:
      return {}
  }
}

const StyledBox = styled(MuiBox, {
  shouldForwardProp: (prop) => prop !== 'variant' && prop !== 'gap' && prop !== 'fullWidth',
})<{ variant?: BoxVariant; gap?: TSpacing; fullWidth?: boolean }>(
  ({ theme, variant, gap, fullWidth }) => getVariantStyles(theme, variant, gap, fullWidth)
)

export const Box = ({ variant, gap, fullWidth, ...props }: BoxProps) => {
  return <StyledBox variant={variant} gap={gap} fullWidth={fullWidth} {...props} />
}
